import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useOrganizationSettings, ParentPortalSettings } from '@/hooks/useOrganizationSettings';
import { toast } from 'sonner';

import { StandardPageHeader } from '@/components/layout/StandardPageHeader';
import { PageContainer } from '@/components/layout/PageContainer';
import { Settings, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ParentPortalFeaturesTab from './ParentPortalFeaturesTab';
import ParentPortalAppearanceTab from './ParentPortalAppearanceTab';
import ParentPortalPermissionsTab from './ParentPortalPermissionsTab';
import ParentPortalNotificationsTab from './ParentPortalNotificationsTab';

const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const ParentPortalSettingsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const organizationId = user?.user_metadata?.organization_id;
  const { organization, loading, updateParentPortalSettings } = useOrganizationSettings(organizationId);

  // Feature settings state
  const [featureSettings, setFeatureSettings] = useState({
    classEnrollments: true,
    onlinePayments: true,
    messaging: true,
    documentAccess: true
  });

  // Appearance settings state
  const [appearanceSettings, setAppearanceSettings] = useState({
    theme: 'default',
    logoEnabled: true,
    customHeaderColor: '#ffffff',
    customAccentColor: '#6366f1'
  });

  // Permissions settings state
  const [permissionSettings, setPermissionSettings] = useState({
    canUpdateProfile: true,
    canViewInvoices: true,
    canRegisterStudents: true,
    canUploadDocuments: true
  });

  // Notifications settings state
  const [notificationSettings, setNotificationSettings] = useState({
    emailEnabled: true,
    smsEnabled: false,
    enrollmentNotifications: true,
    paymentNotifications: true,
    classReminders: true
  });

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (organization?.parent_portal_settings) {
      const settings = organization.parent_portal_settings;
      // Initialize feature settings
      if (settings.features) {
        setFeatureSettings({
          classEnrollments: settings.features.classEnrollments !== false,
          onlinePayments: settings.features.onlinePayments !== false,
          messaging: settings.features.messaging !== false,
          documentAccess: settings.features.documentAccess !== false
        });
      }
      // Initialize appearance settings
      if (settings.appearance) {
        setAppearanceSettings({
          theme: settings.appearance.theme || 'default',
          logoEnabled: settings.appearance.logoEnabled !== false,
          customHeaderColor: settings.appearance.customHeaderColor || '#ffffff',
          customAccentColor: settings.appearance.customAccentColor || '#6366f1'
        });
      }
      // Initialize permission settings
      if (settings.permissions) {
        setPermissionSettings({
          canUpdateProfile: settings.permissions.canUpdateProfile !== false,
          canViewInvoices: settings.permissions.canViewInvoices !== false,
          canRegisterStudents: settings.permissions.canRegisterStudents !== false,
          canUploadDocuments: settings.permissions.canUploadDocuments !== false
        });
      }
      // Initialize notification settings
      if (settings.notifications) {
        setNotificationSettings({
          emailEnabled: settings.notifications.emailEnabled !== false,
          smsEnabled: settings.notifications.smsEnabled === true,
          enrollmentNotifications: settings.notifications.enrollmentNotifications !== false,
          paymentNotifications: settings.notifications.paymentNotifications !== false,
          classReminders: settings.notifications.classReminders !== false
        });
      }
    }
  }, [organization]);

  const handleFeatureToggle = (feature: keyof typeof featureSettings) => {
    setFeatureSettings(prev => ({
      ...prev,
      [feature]: !prev[feature]
    }));
  };

  const handleAppearanceChange = (settings: any) => {
    setAppearanceSettings(prev => ({
      ...prev,
      ...settings
    }));
  };

  const handlePermissionToggle = (field: keyof typeof permissionSettings) => {
    setPermissionSettings(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleNotificationToggle = (field: keyof typeof notificationSettings) => {
    setNotificationSettings(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSaveSettings = async () => {
    if (!updateParentPortalSettings) return;
    setIsSaving(true);
    try {
      const settings: ParentPortalSettings = {
        features: featureSettings,
        appearance: appearanceSettings,
        permissions: permissionSettings,
        notifications: notificationSettings
      };
      await updateParentPortalSettings(settings);
      toast.success("Portal settings saved successfully");
    } catch (error) {
      console.error("Error saving portal settings:", error);
      toast.error("Failed to save portal settings");
    } finally {
      setIsSaving(false);
    }
  };

  const handlePreviewClick = () => {
    window.open("/parent-portal", "_blank");
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <StandardPageHeader
        title="Parent Portal Settings"
        subtitle="Configure how parents interact with your hive"
        icon={Settings}
        actions={
          <div className="flex gap-2">
            <Button onClick={handleSaveSettings} variant="default" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Settings'}
            </Button>
            <Button onClick={handlePreviewClick} variant="outline" className="gap-2">
              <Eye className="h-4 w-4" />
              Preview Portal
            </Button>
          </div>
        }
      />
      
      <PageContainer maxWidth="full">
        <motion.div variants={containerVariants} initial="hidden" animate="visible">
          <Tabs defaultValue="features" className="w-full">
            <TabsList className="grid w-full md:w-auto md:inline-flex grid-cols-2 md:grid-cols-none">
              <TabsTrigger value="features">Features</TabsTrigger>
              <TabsTrigger value="appearance">Appearance</TabsTrigger>
              <TabsTrigger value="permissions">Permissions</TabsTrigger>
              <TabsTrigger value="notifications">Notifications</TabsTrigger>
            </TabsList>
            <TabsContent value="features" className="mt-6">
              <ParentPortalFeaturesTab
                featureSettings={featureSettings}
                onFeatureToggle={handleFeatureToggle}
              />
            </TabsContent>
            <TabsContent value="appearance" className="mt-6">
              <ParentPortalAppearanceTab
                initialSettings={appearanceSettings}
                onSettingsChange={handleAppearanceChange}
              />
            </TabsContent>
            <TabsContent value="permissions" className="mt-6">
              <ParentPortalPermissionsTab
                permissionSettings={permissionSettings}
                onPermissionToggle={handlePermissionToggle}
                isSaving={isSaving}
              />
            </TabsContent>
            <TabsContent value="notifications" className="mt-6">
              <ParentPortalNotificationsTab
                notificationSettings={notificationSettings}
                onNotificationToggle={handleNotificationToggle}
                isSaving={isSaving}
              />
            </TabsContent>
          </Tabs>
        </motion.div>
      </PageContainer>
    </div>
  );
};

export default ParentPortalSettingsPage;
